/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Grid2 from "@mui/material/Grid2";
import { Typography, Container } from "@mui/material";
import androidImage from "../../assets/images/ic-aok.png";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import { getConfig } from "../../config";
import "./ApplicationDetailsAPKFile.css";

const MONTH_NAMES = [
  "Jan", "Feb", "March", "Apr", "May", "June",
  "July", "August", "Sept", "Oct", "Nov", "Dec",
];

function renderTextWithTooltip(text, className = "version-text-binary") {
  const displayText =
    text && text.length > 36 ? `${text.substring(0, 36)}...` : text;
  const needsTooltip = text && text.length > 36;

  if (needsTooltip) {
    return (
      <Tooltip title={text}>
        <p className={className}>{displayText}</p>
      </Tooltip>
    );
  }

  return <p className={className}>{displayText}</p>;
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

function TruncatedName({ name, maxLen, className }) {
  if (name && name.length > maxLen) {
    return (
      <Tooltip title={name}>
        <div className={className}>{`${name.substring(0, maxLen)}...`}</div>
      </Tooltip>
    );
  }
  return <div className={className}>{name}</div>;
}

TruncatedName.propTypes = {
  name: PropTypes.string,
  maxLen: PropTypes.number.isRequired,
  className: PropTypes.string.isRequired,
};

function TruncatedSpan({ text, maxLen, className }) {
  if (text && text.length > maxLen) {
    return (
      <Tooltip title={text}>
        <span className={className}>{`${text.substring(0, maxLen)}...`}</span>
      </Tooltip>
    );
  }
  return <span className={className}>{text}</span>;
}

TruncatedSpan.propTypes = {
  text: PropTypes.string,
  maxLen: PropTypes.number.isRequired,
  className: PropTypes.string.isRequired,
};

function RegularApkSection({ submissionData, binaryData, binaryKey, show, onToggleShow }) {
  const portal = getConfig().REACT_APP_PORTAL;
  const appName =
    portal === "global-admin"
      ? submissionData.binary.fileName || "N/A"
      : submissionData.descriptions?.[0]?.applicationName || "N/A";

  const showKey =
    submissionData.binaries &&
    submissionData.binaries !== null &&
    (portal === "domain-approver" ||
      portal === "global-admin" ||
      (portal === "seller" &&
        (sessionStorage.getItem("isOemSeller") === "true" ||
          sessionStorage.getItem("idpLogin") === "true")));

  const showKeyDate = showKey;

  const keyDateText = (() => {
    if (submissionData?.binaries?.[binaryKey]?.submittedAt) {
      return formatDate(submissionData.binaries[binaryKey].submittedAt);
    }
    if (submissionData?.lastModified) {
      return formatDate(submissionData.lastModified);
    }
    return "N/A";
  })();

  return (
    <div className="Apkscetion">
      <div className="info">
        <div className="application-name-div">
          <img
            className="application-icon-binary-file"
            src={androidImage}
            width={24}
            height={24}
            alt="application-icon"
          />
          {show ? (
            <div className="application">{appName}</div>
          ) : (
            <TruncatedName name={appName} maxLen={30} className="application" />
          )}
        </div>
        {!show && (
          <>
            <div>
              <span className="title-application-details-apkfile">Version</span>
              <TruncatedSpan text={binaryData.versionName} maxLen={22} className="version-text-binary" />
            </div>
            <div>
              <span className="submit-date">Submitted</span>
              <span className="date-text-binary">{formatDate(submissionData.lastModified)}</span>
            </div>
          </>
        )}
        <div className="buttons-list">
          <button className="show-info" onClick={onToggleShow} style={{ cursor: "pointer" }}>
            {show ? "Hide Info" : "Show Info"}
          </button>
          <button className="download" style={{ color: "inherit" }}>
            <a
              href={binaryData ? binaryData.url : ""}
              className="download-link"
              style={{
                color: "inherit !important",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <FileDownloadOutlinedIcon style={{ color: "white", fontSize: "16px", marginRight: "7px" }} />
              Download APK
            </a>
          </button>
        </div>
      </div>
      {show && (
        <div>
          <div className="more-info">
            <div className="vps">
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div>
                  <p className="version-title">Version</p>
                  {renderTextWithTooltip(binaryData.versionName)}
                </div>
                <div>
                  <p className="version-title">Version Code</p>
                  {renderTextWithTooltip(binaryData.versionCode)}
                </div>
                {submissionData.calMetaData && (
                <div>
                  <p className="version-title">Carlib version</p>
                  <p className ="version-text-binary"><span className ="version-text-binary">Min. {submissionData.calMetaData.carAppLibMinVersion}</span><span className ="version-text-binary"> {" "} Max. {submissionData.calMetaData.carAppLibMaxVersion}</span></p>
                  </div>
                )}
                {!submissionData.calMetaData &&showKey && (
                  <div>
                    <p className="version-title">Key</p>
                    {portal === "domain-approver" || portal === "seller" || portal === "global-admin"
                      ? renderTextWithTooltip(binaryKey)
                      : "N/A"}
                  </div>
                )}
                {!submissionData.calMetaData && portal === "oem-admin" && (
                  <div>
                    <p className="version-title">Key</p>
                    {renderTextWithTooltip(submissionData?.appSignatureInfos?.[0]?.signatureKey || "N/A")}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div>
                  <p className="version-title">Package name</p>
                  {renderTextWithTooltip(binaryData.packageName)}
                </div>
                <div>
                  <p className="version-title api-level">API level</p>
                  {renderTextWithTooltip(binaryData.minSdkVersion)}
                </div>
                   {submissionData.calMetaData &&showKey && (
                  <div>
                    <p className="version-title">Key</p>
                    {portal === "domain-approver" || portal === "seller" || portal === "global-admin"
                      ? renderTextWithTooltip(binaryKey)
                      : "N/A"}
                  </div>
                )}
                {submissionData.calMetaData && portal === "oem-admin" && (
                  <div>
                    <p className="version-title">Key</p>
                    {renderTextWithTooltip(submissionData?.appSignatureInfos?.[0]?.signatureKey || "N/A")}
                  </div>
                )}
                {!submissionData.calMetaData && showKeyDate && (
                  <div>
                    <p className="version-title">Key submitted Date</p>
                    <p className="version-text-binary">{keyDateText}</p>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div>
                  <p className="version-title">Size</p>
                  <p className="version-text-binary">
                    {(binaryData.sizeInBytes / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <div>
                  <p className="version-title">Submitted</p>
                  <p className="version-text-binary">{formatDate(submissionData.lastModified)}</p>
                </div>
                 {submissionData.calMetaData && showKeyDate && (
                  <div>
                    <p className="version-title">Key submitted Date</p>
                    <p className="version-text-binary">{keyDateText}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

RegularApkSection.propTypes = {
  submissionData: PropTypes.object.isRequired,
  binaryData: PropTypes.object.isRequired,
  binaryKey: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onToggleShow: PropTypes.func.isRequired,
};

function SellerBundleSection({ submissionData, binaryData, binaryKey, show, onToggleShow }) {
  console.log("submission data", submissionData)
  const portal = getConfig().REACT_APP_PORTAL;
  const fileName = submissionData?.bundle?.fileName;
  const version = submissionData.name;
  console.log("binary data",binaryData)
 
  return (
    <>
    {submissionData.bundle && (
    <div className="Apkscetion">
      <div className="info">
        <div className="application-name-div">
          <img
            className="application-icon-binary-file"
            src={androidImage}
            width={24}
            height={24}
            alt="application-icon"
          />
          <TruncatedName name={fileName} maxLen={36} className="application" />
        </div>
        {!show && (
          <>
            <div>
              <span className="title-application-details-apkfile">Version</span>
              <TruncatedSpan text={version} maxLen={36} className="version-text-binary" />
            </div>
            <div>
              <span className="submit-date">Submitted</span>
              <span className="date-text-binary">{formatDate(submissionData.lastModified)}</span>
            </div>
          </>
        )}
      
          <div className="buttons-list">
          <button className="show-info" onClick={onToggleShow} style={{ cursor: "pointer" }}>
            {show ? "Hide Info" : "Show Info"}
          </button>
        </div>
      
      </div>
      {show && (
        <div>
          {submissionData &&
            Object.keys(submissionData).length > 0 &&
            submissionData.bundle &&
            submissionData?.bundle?.binaries.length > 0 && (
              <div className="more-info">
                {submissionData.bundle.binaries.map((data, index) => (
                  <div
                    key={data.packageName || data.versionCode || index}
                    style={{ borderBottom: "1px solid rgba(18, 20, 21, 0.2)", marginBottom: "10px !important" }}
                  >
                    <div className="vps">
                      <div>
                        <div>
                          <p className="version-title">Version</p>
                          {renderTextWithTooltip(data.fileName)}
                        </div>
                        <div>
                          <p className="version-title">Version Code</p>
                          {renderTextWithTooltip(data.versionCode)}
                        </div>
                        <div>
                          <p className="version-title">Key</p>
                          {portal === "domain-approver" || portal === "global-admin"
                            ? renderTextWithTooltip(binaryKey)
                            : renderTextWithTooltip(submissionData?.appSignatureInfos?.[0]?.signatureKey || "N/A")}
                        </div>
                      </div>
                      <div>
                        <div>
                          <p className="version-title">Package name</p>
                          {renderTextWithTooltip(data.packageName)}
                        </div>
                        <div>
                          <p className="version-title api-level">API level</p>
                          {renderTextWithTooltip(data.minSdkVersion)}
                        </div>
                        <div>
                          <p className="version-title">Key submitted Date</p>
                          <p className="version-text-binary">
                            {submissionData?.appSignatureInfos?.[0]?.submissionDate
                              ? formatDate(submissionData.appSignatureInfos[0].submissionDate)
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                      <div>
                        <div>
                          <p className="version-title">Size</p>
                          <p className="version-text-binary">
                            {(data.sizeInBytes / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <div>
                          <p className="version-title">Submitted</p>
                          <p className="version-text-binary">{formatDate(submissionData.lastModified)}</p>
                        </div>
                      </div>
                      <div></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
    )}
    </>
  );
}

SellerBundleSection.propTypes = {
  submissionData: PropTypes.object.isRequired,
  binaryData: PropTypes.object.isRequired,
  binaryKey: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onToggleShow: PropTypes.func.isRequired,
};

function renderThirdPartyKeyOrCarlibColumn({ submissionData, binaryKey, isOemAdminOrDA }) {
  const hasBinaries = submissionData.binaries && submissionData.binaries !== null;
  const hasCarAppLib =
    submissionData.carAppLibMaxVersion && submissionData.carAppLibMinVersion;

  return (
    <div>
      {isOemAdminOrDA && hasBinaries && (
        <>
          <p className="version-title">Key</p>
          {renderTextWithTooltip(binaryKey)}
        </>
      )}
      {isOemAdminOrDA && !submissionData.carAppLibMaxVersion && !submissionData.carAppLibMinVersion && (
        <>
          <p className="version-title">Key</p>
          {renderTextWithTooltip(submissionData?.appSignatureInfos?.[0]?.signatureKey || "N/A")}
        </>
      )}
      {isOemAdminOrDA && hasCarAppLib && (
        <>
          <p className="version-title">Carlib Version</p>
          <p className="version-text-binary">
            <span className="version-text-binary">Min. {submissionData.carAppLibMinVersion}</span>
            <span className="version-text-binary"> {" "} Max. {submissionData.carAppLibMaxVersion}</span>
          </p>
        </>
      )}
    </div>
  );
}

function renderThirdPartyMiddleKeyOrDate({ submissionData, showKeyDate }) {
  const hasCarAppLib =
    submissionData.carAppLibMaxVersion && submissionData.carAppLibMinVersion;
  const submissionDate = submissionData?.appSignatureInfos?.[0]?.submissionDate;

  if (showKeyDate && !hasCarAppLib) {
    return (
      <div>
        <p className="version-title">Key submitted Date </p>
        <p className="version-text-binary">
          {submissionDate ? formatDate(submissionDate) : "N/A"}
        </p>
      </div>
    );
  }

  if (hasCarAppLib) {
    return (
      <div>
        <p className="version-title">Key</p>
        {renderTextWithTooltip(submissionData?.appSignatureInfos?.[0]?.signatureKey || "N/A")}
      </div>
    );
  }

  return null;
}

function ThirdPartyAppsSection({ submissionData, binaryKey, show, onToggleShow }) {
  const portal = getConfig().REACT_APP_PORTAL;
  const isOemAdmin = portal === "oem-admin";
  const isOemAdminOrDA = isOemAdmin || portal === "domain-approver";
  const hasCarAppLib =
    submissionData.carAppLibMaxVersion && submissionData.carAppLibMinVersion;
  const appName = isOemAdmin
    ? submissionData.descriptions["en_US"]?.title
    : submissionData.descriptions[0]?.applicationName;
  const version = isOemAdmin ? submissionData.contentVersion : submissionData.name;
  const dateStr = isOemAdmin ? submissionData.statusChangeDate : submissionData.lastModified;
  const submissionDate = submissionData?.appSignatureInfos?.[0]?.submissionDate;

  const showKeyDate =
    isOemAdmin ||
    (portal === "domain-approver" && submissionData.binaries && submissionData.binaries !== null);

  return (
    <div className="Apkscetion">
      <div className="info">
        <div className="application-name-div">
          <img
            className="application-icon-binary-file"
            src={androidImage}
            width={24}
            height={24}
            alt="application-icon"
          />
          <TruncatedName name={appName} maxLen={30} className="application" />
        </div>
        {!show && (
          <>
            <div className="apksection-version">
              <span className="title-apksection-version">Version</span>
              <TruncatedSpan text={version} maxLen={36} className="version-text-binary" />
            </div>
            <div>
              <span className="submit-date">Submitted</span>
              <span className="date-text-binary">{formatDate(dateStr)}</span>
            </div>
          </>
        )}
        <div className="buttons-list">
          <button className="show-info" onClick={onToggleShow} style={{ cursor: "pointer" }}>
            {show ? "Hide Info" : "Show Info"}
          </button>
          <button className="download" style={{ color: "inherit" }}>
            <a
              href={submissionData.apkUrl ? submissionData.apkUrl : ""}
              className="download-link"
              style={{
                color: "inherit !important",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <FileDownloadOutlinedIcon style={{ color: "white", fontSize: 15, marginRight: 3, marginTop: 2 }} />
              Download
            </a>
          </button>
        </div>
      </div>
      {show && (
        <div>
          <div className="more-info">
            <div className="vps">
              <div>
                <div>
                  <p className="version-title">Version</p>
                  {renderTextWithTooltip(version)}
                </div>
                <div>
                  <p className="version-title">Version Code domain</p>
                  {renderTextWithTooltip(submissionData.versionCode)}
                </div>
                {renderThirdPartyKeyOrCarlibColumn({ submissionData, binaryKey, isOemAdminOrDA })}
              </div>
              <div>
                <div>
                  <p className="version-title">Package name</p>
                  {renderTextWithTooltip(isOemAdmin ? submissionData.packageId : submissionData.packageName)}
                </div>
                <div>
                  <p className="version-title api-level">API level</p>
                  {renderTextWithTooltip(isOemAdmin ? submissionData.androidApiLevel : submissionData.minSdkVersion)}
                </div>
                {renderThirdPartyMiddleKeyOrDate({ submissionData, showKeyDate })}
              </div>
              <div>
                <div>
                  <p className="version-title">Size</p>
                  <p className="version-text-binary">
                    {submissionData.downloadSize && (
                      <span>{(submissionData.downloadSize / 1024 / 1024).toFixed(2)} MB</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="version-title">Submitted</p>
                  <p className="version-text-binary">{formatDate(dateStr)}</p>
                </div>
                {showKeyDate && hasCarAppLib && (
                  <div>
                    <p className="version-title">Key submitted Date </p>
                    <p className="version-text-binary">
                      {submissionDate ? formatDate(submissionDate) : "N/A"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ThirdPartyAppsSection.propTypes = {
  submissionData: PropTypes.object.isRequired,
  binaryKey: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onToggleShow: PropTypes.func.isRequired,
};

function OemBundleSection({ submissionData, binaryKey, show, onToggleShow }) {
  const portal = getConfig().REACT_APP_PORTAL;
  const fileName = submissionData.apks?.[0]?.filename || "N/A";
  const version = submissionData.apks?.[0]?.contentVersion || "N/A";

  return (
    <div className="Apkscetion">
      <div className="info">
        <div className="application-name-div">
          <img
            className="application-icon-binary-file"
            src={androidImage}
            width={24}
            height={24}
            alt="application-icon"
          />
          <TruncatedName name={fileName} maxLen={30} className="application" />
        </div>
        {!show && (
          <>
            <div className="apksection-version">
              <span className="title-apksection-version">Version</span>
              <TruncatedSpan text={version} maxLen={36} className="version-text-binary" />
            </div>
            <div>
              <span className="submit-date">Submitted</span>
              <span className="date-text-binary">{formatDate(submissionData.statusChangeDate)}</span>
            </div>
          </>
        )}
        <div className="buttons-list">
          <button className="show-info" onClick={onToggleShow} style={{ cursor: "pointer" }}>
            {show ? "Hide Info" : "Show Info"}
          </button>
          <button className="download" style={{ color: "inherit" }}>
            <a
              href={submissionData.apkUrl ? submissionData.apkUrl : ""}
              className="download-link"
              style={{
                color: "inherit !important",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <FileDownloadOutlinedIcon style={{ color: "white", fontSize: 15, marginRight: 3, marginTop: 2 }} />
              Download
            </a>
          </button>
        </div>
      </div>
      {show &&
        submissionData.apks?.map((apkData) => (
          <div key={apkData.packageId || apkData.contentVersion || apkData.versionCode}>
            <div className="more-info">
              <div className="vps">
                <div>
                  <div>
                    <p className="version-title">Version</p>
                    {renderTextWithTooltip(apkData?.contentVersion || "N/A")}
                  </div>
                  <div>
                    <p className="version-title">Version Code</p>
                    {renderTextWithTooltip(apkData?.versionCode || "N/A")}
                  </div>
                  <div>
                    <p className="version-title">Key</p>
                    {portal === "domain-approver"
                      ? renderTextWithTooltip(binaryKey)
                      : renderTextWithTooltip(submissionData?.appSignatureInfos?.[0]?.signatureKey || "N/A")}
                  </div>
                </div>
                <div>
                  <div>
                    <p className="version-title">Package name</p>
                    {renderTextWithTooltip(apkData?.packageId || "N/A")}
                  </div>
                  <div>
                    <p className="version-title api-level">API level</p>
                    {renderTextWithTooltip(submissionData.androidApiLevel)}
                  </div>
                  <div>
                    <p className="version-title">Key submitted Date</p>
                    <p className="version-text-binary">
                      {submissionData?.appSignatureInfos?.[0]?.submissionDate
                        ? formatDate(submissionData.appSignatureInfos[0].submissionDate)
                        : "N/A"}
                    </p>
                  </div>
                </div>
                <div>
                  <div>
                    <p className="version-title">Size</p>
                    <p className="version-text-binary">
                      {submissionData.downloadSize && (
                        <span>{(submissionData.downloadSize / 1024 / 1024).toFixed(2)} MB</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="version-title">Submitted</p>
                    <p className="version-text-binary">{formatDate(submissionData.statusChangeDate)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}

OemBundleSection.propTypes = {
  submissionData: PropTypes.object.isRequired,
  binaryKey: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onToggleShow: PropTypes.func.isRequired,
};

function processSubmissionBinaryData(submissionData) {
  if (!submissionData) return { binaryData: null, showApkFile: false, binaryKey: "" };
  const binary = submissionData.binary;
  const binaryData = binary && Object.keys(binary).length > 0 ? binary : null;
  const showApkFile = !!binaryData;
  const binaryKey = submissionData.binaries
    ? Object.keys(submissionData.binaries)[0] || ""
    : "";
  return { binaryData, showApkFile, binaryKey };
}

function ApplicationDetailsAPKFile({ submissionData, createNewRelase }) {
  const [show, setShow] = useState(false);
  const [binaryData, setBinaryData] = useState({});
  const [showApkFile, setShowApkFile] = useState(true);
  const [binaryKey, setBinaryKey] = useState("");

  const binary = useSelector((state) => state.binaryDetails.binary);

  useEffect(() => {
    const result = processSubmissionBinaryData(submissionData);
    if (result.binaryData) setBinaryData(result.binaryData);
    setShowApkFile(result.showApkFile);
    setBinaryKey(result.binaryKey);
  }, [submissionData]);

  useEffect(() => {
    if (binary && Object.keys(binary).length > 0) {
      setBinaryData(binary);
      setShowApkFile(true);
    } else {
      setShowApkFile(false);
    }
  }, [binary]);

  useEffect(() => {
    setShowApkFile(!createNewRelase);
  }, [createNewRelase]);

  const showInfo = () => {
    setShow((prevState) => !prevState);
  };

  const getSectionStyle = () => {
    if (submissionData.status === "DRAFT") {
      return { marginTop: "30px", width: "85%" };
    }
    if (sessionStorage.getItem("idpLogin") === "true" || sessionStorage.getItem("isOemSeller")) {
      return { marginTop: "30px" };
    }
    return { marginTop: "0px" };
  };

  const hasBinaryData =
    (binaryData && Object.keys(binaryData).length > 0) ||
    (submissionData.applicationType === "BUNDLE" && !!submissionData.bundle) ||
    (!!submissionData.contentType && Object.keys(submissionData).length > 0);

  const portal = getConfig().REACT_APP_PORTAL;
  const isThirdPartyApps = globalThis.location.href.includes("third-party-apps");
  const hasBinary = binaryData && Object.keys(binaryData).length > 0;
  const hasSubmissionData = submissionData && Object.keys(submissionData).length > 0;

  const renderSection = () => {
    const sectionProps = { submissionData, binaryData, binaryKey, show, onToggleShow: showInfo };

    if (portal === "seller" || portal === "global-admin" || (portal === "domain-approver" && !isThirdPartyApps)) {
      if (hasBinary && showApkFile && submissionData.applicationType !== "BUNDLE") {
        return <RegularApkSection {...sectionProps} />;
      }
      if (submissionData.applicationType === "BUNDLE") {
        return <SellerBundleSection {...sectionProps} />;
      }
    }

    if (portal === "oem-admin" || (portal === "domain-approver" && isThirdPartyApps)) {
      if (hasSubmissionData && submissionData.contentType === "app") {
        return <ThirdPartyAppsSection {...sectionProps} />;
      }
    }

    if ((portal === "oem-admin" || (portal === "domain-approver" && !submissionData.isOemApp)) &&
        hasSubmissionData && submissionData.contentType === "bundle") {
      return <OemBundleSection {...sectionProps} />;
    }

    return null;
  };

  return (
    <Container maxWidth="lg">
      <Grid2 container justifyContent="flex-start" alignItems="center">
        <Grid2 size={12}>
          <section
            id="apkFile"
            className="apkfilesectyion"
            style={getSectionStyle()}
          >
            {showApkFile && hasBinaryData && (
              <Typography
                sx={{
                  "font-weight": "bold !important",
                  "font-size": "20px !important",
                  color: "rgba(18, 20, 21, 1) !important",
                }}
                className="uploadSignedApkText"
              >
                APK Files
              </Typography>
            )}
            {renderSection()}
          </section>
        </Grid2>
      </Grid2>
    </Container>
  );
}

ApplicationDetailsAPKFile.propTypes = {
  submissionData: PropTypes.shape({
    lastModified: PropTypes.string,
    applicationType: PropTypes.string,
    binary: PropTypes.object,
    bundle: PropTypes.object,
    binaries: PropTypes.object,
    status: PropTypes.string,
    descriptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    contentType: PropTypes.string,
    name: PropTypes.string,
    versionCode: PropTypes.string,
    packageName: PropTypes.string,
    minSdkVersion: PropTypes.string,
    apkUrl: PropTypes.string,
    contentVersion: PropTypes.string,
    packageId: PropTypes.string,
    androidApiLevel: PropTypes.string,
    downloadSize: PropTypes.number,
    statusChangeDate: PropTypes.string,
    isOemApp: PropTypes.bool,
    apks: PropTypes.arrayOf(PropTypes.object),
    appSignatureInfos: PropTypes.arrayOf(
      PropTypes.shape({
        signatureKey: PropTypes.string,
        submissionDate: PropTypes.string,
      })
    ),
  }),
  createNewRelase: PropTypes.bool,
};

export default ApplicationDetailsAPKFile;
